import React, { useState } from 'react';
import {
  Alert,
  Dropdown,
  Menu,
  Modal,
  Switch,
  Table,
  Tag,
  Tooltip,
  message
} from 'antd';
import { api } from '@unicus/react-ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DisplayAmount,
  InvoiceIcon,
  ListTable,
  ShowFilesModal
} from 'components';
import moment from 'moment';
import { formartDateTimeUTC, formatCurrency } from 'util/formatters';
import {
  cancelInvoice,
  deleteTransfer,
  editInvoice,
  openResendInvoice,
  openViewDetail,
  syncInvoice
} from 'stores/financialEntries/financialEntryActions';
import { INITIALBALANCE, PAYMENT, RECEIPT, RECONCILED } from 'constants/types';
import display from 'constants/display';

import {
  CloseCircleOutlined,
  CloudSyncOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PaperClipOutlined,
  SendOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import useCompany from 'core/hooks/useCompany';
import { FaFileInvoiceDollar, FaHandHoldingUsd } from 'react-icons/fa';
import InvoiceHelper from 'helpers';
import useAuth from 'core/hooks/useAuth';
import useSegmentField from 'core/hooks/useSegmentField';
import isNullOrEmpty from 'util/functions/isNullOrEmpty';
import generateUUID from 'util/functions/generateUUID';
import FinancialEntryResendEmail from './FinancialEntryResendEmail';
import FinancialEntryViewDetail from './FinancialEntryViewDetail';

const FinancialEntryListTable = ({
  disableEditing,
  hiddenButtonActions = [],
  ...props
}) => {
  const { accountantId, isAdmin } = useAuth();
  const segmentFields = useSegmentField();
  const { company, userCompany } = useCompany();
  const dispatch = useDispatch();
  const [openFiles, setOpenFiles] = useState();

  const { queryParams } = useSelector(
    (c) => c.resources['financial-entries'] ?? {}
  );

  const { dateType } = queryParams ?? {};

  const getColorByType = (type) => {
    switch (type) {
      case RECEIPT:
        return 'green';
      case PAYMENT:
        return 'red';
      case INITIALBALANCE:
        return 'gray';
      default:
        return '';
    }
  };

  const renderPaymentIcon = (record) => {
    if (record.status === 'paid') {
      const amount = ['dateOfCompensation', 'paymentDate'].includes(dateType)
        ? record.amount
        : record.payment?.netAmount;

      let title = `Baixa registrada em ${moment(record.payment?.date).format(
        'DD/MM/YYYY [ás] HH:mm'
      )} no valor de ${formatCurrency(amount)} no ${
        display[record.payment.method]
      }.`;

      if (record.payment?.uniqueSequentialNumber) {
        title += ` NSU ${record.payment.uniqueSequentialNumber}`;
      }

      return (
        <Tooltip title={title}>
          <FaHandHoldingUsd />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Aguardando pagamento">
        <FontAwesomeIcon
          style={{ marginLeft: '5px', marginRight: '5px' }}
          className="icon"
          icon="clock"
        />
      </Tooltip>
    );
  };

  const renderDeletedIcon = (record) => {
    if (!record.financialEntry.isDeleted) {
      return null;
    }

    return (
      <Tooltip
        title={`Documento excluído em ${moment(
          record.financialEntry?.lastModificationTime
        ).format('DD/MM/YYYY [ás] HH:mm')}.`}
      >
        <CloseCircleOutlined />
      </Tooltip>
    );
  };

  const renderExportedIcon = (record) => {
    const {
      financialEntry: { exportAudit: auditedExportDocument },
      exportAudit: auditedExportItem
    } = record;

    const messages = [];

    const isChanged =
      auditedExportItem?.isModified ||
      auditedExportDocument?.isModified ||
      false;

    const generateMessage = (title, date, user) => {
      const formattedData = moment(date).format('DD/MM/YYYY [ás] HH:mm');
      messages.push(
        `${title} contabilizado em ${formattedData} por ${user?.name}.`
      );
    };

    if (auditedExportItem) {
      generateMessage(
        record.type === RECEIPT ? 'Recebimento' : 'Pagamento',
        auditedExportItem.date,
        auditedExportItem?.user
      );
    }

    if (auditedExportDocument && messages.length === 0) {
      generateMessage(
        'Documento',
        auditedExportDocument.date,
        auditedExportDocument?.user
      );
    }

    if (messages.length === 0) {
      return null;
    }

    const className = isChanged ? 'gx-text-warning' : '';

    return (
      <Tooltip
        title={messages.map((c) => (
          <div key={generateUUID()}>{c}</div>
        ))}
      >
        <FontAwesomeIcon
          className={`gx-ml-1 gx-mr-1 ${className}`}
          icon="calculator"
        />
      </Tooltip>
    );
  };

  const handleSendEmail = (record) => {
    dispatch(openResendInvoice(record));
  };

  const handleViewDetail = ({ financialEntry }) => {
    dispatch(openViewDetail(financialEntry.id));
  };

  const handleOpenFiles = (attachments) => {
    setOpenFiles(attachments.map((c) => c.attachment));
  };

  const renderReconciledIcon = ({ transaction }) => {
    if (!transaction || transaction.status !== RECONCILED) {
      return null;
    }

    const { dateOfConciliation, userOfConciliation } = transaction;

    const formattedData = moment(dateOfConciliation).format(
      'DD/MM/YYYY [ás] HH:mm'
    );
    return (
      <Tooltip
        title={`Conciliado via arquivo OFX em ${formattedData} por ${userOfConciliation?.name}.`}
      >
        <FontAwesomeIcon
          className="gx-ml-1 gx-mr-2"
          icon={{ prefix: 'far', iconName: 'check-circle' }}
        />
      </Tooltip>
    );
  };

  const handleEditInvoice = ({ financialEntry }) => {
    dispatch(editInvoice(financialEntry.id));
  };

  const handleCancelInvoice = ({ financialEntry }) => {
    let internalCancel = false;

    Modal.confirm({
      width: 450,
      title: 'Deseja cancelar a nota fiscal?',
      content: (
        <div className="gx-mt-3 gx-mb-3">
          <Alert
            action={
              <Switch
                onChange={(checked) => {
                  internalCancel = checked;
                }}
              />
            }
            className="gx-mt-3"
            message={
              <div>
                <div className="gx-mb-1">
                  <strong>Cancelamento interno</strong>
                </div>
                <span>
                  O cancelamento interno só deve ser usado se a prefeitura da
                  sua cidade não permite cancelar nota fiscal via sistema
                  integrado. Nesse caso, você deve cancelar a nota fiscal direto
                  no site da prefeitura e fazer o cancelamento interno no
                  sistema. Deseja prosseguir?
                </span>
              </div>
            }
          />
        </div>
      ),
      okText: 'Sim',
      cancelText: 'Não',
      onOk: () => {
        dispatch(cancelInvoice(financialEntry.id, internalCancel));
      }
    });
  };

  const handleDownloadXml = ({ financialEntry }) => {
    const { urlXml } = financialEntry?.invoice ?? {};

    if (urlXml) {
      window.open(`${urlXml}?download=1`, '_blank').focus();
    }
  };

  const handleDownloadPdf = ({ financialEntry }) => {
    const { urlPdf } = financialEntry?.invoice ?? {};

    if (urlPdf) {
      window.open(urlPdf, '_blank').focus();
    } else {
      message.error('Nota fiscal não possui url para baixar o pdf');
    }
  };

  const allowIssueInvoice = ({ financialEntry }) => {
    const {
      type,
      effectiveDate,
      invoice,
      transferInfo,
      status,
      isDeleted
    } = financialEntry;

    if (isDeleted || transferInfo || status === 'canceled') {
      return false;
    }

    return (
      type === RECEIPT &&
      effectiveDate &&
      (invoice?.status !== undefined
        ? !InvoiceHelper.isReadOnly(invoice?.status)
        : true)
    );
  };

  const invoiceIsAuthorized = ({ financialEntry }) => {
    return (
      financialEntry.isDeleted === false &&
      financialEntry.invoice?.status === 'authorized' &&
      financialEntry.status !== 'canceled'
    );
  };

  const allowAsyncInvoice = ({ financialEntry }) => {
    return (
      isAdmin() &&
      (financialEntry.invoice?.status === 'enqueued' ||
        financialEntry.invoice?.processingDetail?.status === 'processing')
    );
  };

  const handleSyncInvoice = ({ financialEntry }) => {
    dispatch(syncInvoice(financialEntry.invoice?.id));
  };

  const handleCancelTransfer = ({ financialEntry }) => {
    if (financialEntry?.transferInfo?.transactionId) {
      Modal.confirm({
        title: 'Deseja cancelar essa transferência?',
        cancelText: 'Voltar',
        okText: 'Sim',
        onOk: () => {
          dispatch(deleteTransfer(financialEntry?.transferInfo?.transactionId));
        }
      });
    }
  };

  const allowViewInvoice = ({ financialEntry }) => {
    const { type, invoice } = financialEntry;
    return (
      type === RECEIPT && invoice && InvoiceHelper.isReadOnly(invoice?.status)
    );
  };

  const handleClone = ({ financialEntry }, _, initCreation) => {
    api.get('financial-entries', financialEntry.id).then(({ data }) => {
      delete data.result.id;
      delete data.result.status;
      delete data.result.attachments;
      delete data.result.invoice;
      delete data.result.transferInfo;
      delete data.result.recurring;

      data.result.items = [];

      initCreation(data.result);
    });
  };

  const buttons = [
    {
      key: '1',
      title: 'Detalhes',
      icon: <EyeOutlined />,
      onClick: handleViewDetail,
      allowShow: () => true
    },
    {
      key: '2',
      title: 'Emitir nota fiscal',
      icon: <FaFileInvoiceDollar style={{ marginRight: '6px' }} />,
      onClick: handleEditInvoice,
      allowShow: allowIssueInvoice
    },
    {
      key: '3',
      title: 'Visualizar nota fiscal',
      icon: <EyeOutlined />,
      onClick: handleEditInvoice,
      allowShow: allowViewInvoice
    },
    {
      key: '4',
      title: 'Cancelar nota fiscal',
      icon: <CloseCircleOutlined />,
      onClick: handleCancelInvoice,
      allowShow: invoiceIsAuthorized
    },
    {
      key: '5',
      title: `Enviar para o ${segmentFields.serviceRecipient.toLowerCase()}`,
      icon: <SendOutlined />,
      onClick: handleSendEmail,
      allowShow: invoiceIsAuthorized
    },
    {
      key: '6',
      title: 'Baixar XML',
      icon: <DownloadOutlined />,
      onClick: handleDownloadXml,
      allowShow: invoiceIsAuthorized
    },

    {
      key: '12',
      title: 'Baixar PDF',
      icon: <DownloadOutlined />,
      onClick: handleDownloadPdf,
      allowShow: invoiceIsAuthorized
    },

    {
      key: '7',
      title: 'Editar',
      icon: <EditOutlined />,
      onClick: ({ financialEntry, transaction }, initEditing) => {
        if (transaction) {
          Modal.info({
            title:
              'Lançamento já conciliado. Para editar ou excluir esse lançamento, primeiro desfaça a conciliação.'
          });

          return;
        }
        initEditing(financialEntry);
      },
      allowShow: ({ financialEntry }) =>
        !disableEditing &&
        financialEntry.isDeleted === false &&
        !financialEntry.transferInfo &&
        financialEntry.status !== 'canceled'
    },
    {
      key: '12',
      title: 'Clonar',
      icon: <CopyOutlined />,
      onClick: handleClone
    },
    {
      key: '8',
      title: 'Sincronizar nota fiscal',
      icon: <CloudSyncOutlined />,
      onClick: handleSyncInvoice,
      allowShow: allowAsyncInvoice
    },
    {
      key: '9',
      title: 'Cancelar transferência',
      icon: <CloseCircleOutlined />,
      onClick: handleCancelTransfer,
      allowShow: ({ financialEntry }) =>
        financialEntry.isDeleted === false &&
        !!financialEntry.transferInfo &&
        financialEntry.status !== 'canceled'
    }
  ].filter((c) => !hiddenButtonActions.includes(c.title));

  return (
    <>
      {openFiles && (
        <ShowFilesModal files={openFiles} onClose={() => setOpenFiles(null)} />
      )}
      <FinancialEntryResendEmail />
      <FinancialEntryViewDetail />
      <ListTable doubleClick={false} {...props}>
        {({ initEditing, initCreation }) => (
          <>
            <Table.Column
              dataIndex={['financialEntry', 'creationTime']}
              title="Lançamento"
              render={(creationTime) =>
                formartDateTimeUTC(creationTime, 'DD/MM/YYYY')
              }
            />
            <Table.Column
              dataIndex={['financialEntry', 'effectiveDate']}
              title="Competência"
              render={(effectiveDate) =>
                formartDateTimeUTC(effectiveDate, 'DD/MM/YYYY')
              }
            />
            <Table.Column
              dataIndex="dueDate"
              title="Vencimento"
              render={(dueDate, { description }) => (
                <>
                  <div>{formartDateTimeUTC(dueDate, 'DD/MM/YYYY')}</div>
                  <div className="gx-fs-sm gx-text-grey">{description}</div>
                </>
              )}
            />
            <Table.Column
              dataIndex={['payment', 'date']}
              title="Baixa"
              render={(date) =>
                date ? formartDateTimeUTC(date, 'DD/MM/YYYY') : null
              }
            />
            <Table.Column
              dataIndex={['financialEntry', 'description']}
              title="Descrição"
              render={(_, { financialEntry }) => {
                const {
                  description,
                  patient,
                  supplier,
                  customer,
                  transferInfo
                } = financialEntry;

                return (
                  <div>
                    <div>{description}</div>
                    {(patient || supplier || customer || transferInfo) && (
                      <div className="gx-fs-sm gx-text-grey">
                        {patient?.name ?? supplier?.name ?? customer?.name}
                        {transferInfo
                          ? [
                              transferInfo.origin?.name,
                              transferInfo.destination?.name
                            ].join(' / ')
                          : null}
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <Table.Column
              dataIndex={['financialEntry', 'category', 'name']}
              title="Categoria"
              render={(_, { financialEntry, checkingAccount, status }) => {
                const { category, costCenter } = financialEntry;
                return (
                  <span>
                    <span>{category.name}</span>
                    <div className="gx-fs-sm gx-text-grey">
                      {[
                        costCenter?.name,
                        status === 'paid' ? checkingAccount?.name : null
                      ]
                        .filter((c) => !!c)
                        .join(' / ')}
                    </div>
                  </span>
                );
              }}
            />
            <Table.Column
              dataIndex={
                ['dateOfCompensation', 'paymentDate'].includes(dateType)
                  ? ['payment', 'netAmount']
                  : 'amount'
              }
              title="Valor"
              align="right"
              render={(amount) => <DisplayAmount amount={amount} />}
            />
            <Table.Column
              dataIndex={['financialEntry', 'type']}
              title="Tipo"
              width={100}
              render={(type) => (
                <Tag color={getColorByType(type)}>{display[type]}</Tag>
              )}
            />
            <Table.Column
              dataIndex="payment"
              title="Situação"
              width={100}
              render={(_, record) =>
                !record.financialEntry.isDeleted ? (
                  <div>
                    {renderPaymentIcon(record)}
                    {renderExportedIcon(record)}
                    {accountantId &&
                      record?.financialEntry?.category?.hasAccountingIssues && (
                        <Tooltip title="Regra contábil não configurada para a categoria informada. Favor contactar a contabilidade.">
                          <WarningOutlined className="gx-text-warning gx-ml-1 gx-mr-1" />
                        </Tooltip>
                      )}
                    {renderReconciledIcon(record)}
                    {record.financialEntry?.invoice && (
                      <InvoiceIcon {...record.financialEntry.invoice} />
                    )}
                    {record.financialEntry?.attachments?.length > 0 ? (
                      <PaperClipOutlined
                        onClick={() =>
                          handleOpenFiles(record.financialEntry?.attachments)
                        }
                      />
                    ) : null}
                  </div>
                ) : (
                  renderDeletedIcon(record)
                )
              }
            />

            <Table.Column
              width={50}
              render={(_, record) =>
                record.financialEntry.type !== INITIALBALANCE ? (
                  <Dropdown
                    trigger="click"
                    overlay={
                      <Menu>
                        {buttons
                          .filter((c) =>
                            c.allowShow ? c.allowShow(record) : true
                          )
                          .map((c) => (
                            <Menu.Item
                              key={c.key}
                              onClick={() =>
                                c.onClick(record, initEditing, initCreation)
                              }
                              icon={c.icon}
                            >
                              {c.title}
                            </Menu.Item>
                          ))}
                      </Menu>
                    }
                  >
                    <i className="gx-icon-btn icon icon-ellipse-v ant-dropdown-trigger ant-dropdown-open gx-text-grey" />
                  </Dropdown>
                ) : null
              }
            />
          </>
        )}
      </ListTable>
    </>
  );
};

export default FinancialEntryListTable;
